/** @format */

import React, { useEffect } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { loadFlags } from "./features/featureFlag/featureFlagSlice";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { useAnalytics } from "use-analytics";
import Header from "./features/header/Header";
import Notifications from "./features/notifications/Notifications";
import useDocumentTitle from "./utils/hooks/useDocumentTitle";
import "./App.scss";
import { selectIsPrintable } from "./features/printable/printableSlice";
import { useGetMeQuery } from "./app/services/userApi";
import { setUserInfo, setUserPending } from "./auth/authSlice";
import MaintenancePage from "./admin/MaintenancePage";
import {
  SCROLL_TO_QUERY_PARAM_ID,
  setScrollTo,
} from "./features/activityCards/CardSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { findCookie } from "./utils/FindCookie";

const App: React.FC = () => {
  const { page } = useAnalytics();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isPrintable = useAppSelector(selectIsPrintable());
  const [searchParams] = useSearchParams();

  const isMaintenance = useAppSelector((state) => state.admin.isMaintenance);
  const maintenanceMessage = useAppSelector((state) => state.admin.message);

  useDocumentTitle("site title");

  const {
    data: user,
    isSuccess: isUserSuccess,
    isFetching: isUserFetching,
  } = useGetMeQuery(
    findCookie("auth") !== undefined ||
      document.location.hostname === "localhost" // always send the request if local as the cookie won't be found
      ? undefined
      : skipToken,
  );

  // if the request is a success, the user has a valid auth cookie and the recieved details can be saved to state
  useEffect(() => {
    // whether user is logged in or not, set pending false
    if (!isUserFetching) {
      dispatch(setUserPending(false));
    }
    if (isUserSuccess && user !== undefined && user !== null) {
      dispatch(setUserInfo(user));
    }
  }, [user, isUserSuccess, isUserFetching, dispatch]);

  useEffect(() => {
    dispatch(loadFlags());
  }, [dispatch]);

  useEffect(() => {
    page();
  }, [page, location]);

  useEffect(() => {
    if (document.getElementsByClassName("page-container")?.[0] !== undefined)
      document.getElementsByClassName("page-container")[0].scrollTo(0, 0);
  }, [location]);

  // Save scrollTo query param to state so that it can be consumed by a component and scrolled to
  useEffect(() => {
    const scrollId = searchParams.get(SCROLL_TO_QUERY_PARAM_ID);
    if (scrollId !== null) dispatch(setScrollTo(scrollId));
  }, [searchParams, dispatch]);

  if (isMaintenance) return <MaintenancePage message={maintenanceMessage} />;

  return (
    <>
      <div
        id="app-container"
        className={`${isPrintable ? "h-100 d-block" : ""}`}
      >
        <Header />
        <Notifications />
        <Outlet />
      </div>
    </>
  );
};

export default App;
